import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { normalizeObjectStrings } from '../lib/utils';
import BrandLogo from '../components/BrandLogo';
import {
  AlertCircle,
  ArrowUpRight,
  BadgeCheck,
  Eye,
  LogOut,
  MapPin,
  Save,
  Sparkles,
  Store,
  Users,
} from 'lucide-react';
import { BRAZIL_STATES, SUPPLIER_CATEGORIES, SUPPLIER_PLAN_OPTIONS, getSupplierPlanMeta } from '../lib/suppliers';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

const APPROVAL_META = {
  pending_review: {
    title: 'Seu perfil esta em analise',
    description: 'A equipe esta validando o cadastro antes de liberar a publicacao no diretorio.',
    className: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: AlertCircle,
  },
  rejected: {
    title: 'Seu perfil precisa de ajustes',
    description: 'Revise as informacoes e atualize o cadastro para uma nova analise.',
    className: 'border-red-200 bg-red-50 text-red-900',
    icon: AlertCircle,
  },
  approved: {
    title: 'Perfil aprovado',
    description: 'Seu cadastro foi aprovado. Se a publicacao estiver ativa, voce ja pode aparecer no diretorio.',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    icon: BadgeCheck,
  },
};

export default function SupplierDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    name: '',
    phone: '',
    category: '',
    city: '',
    state: '',
    short_description: '',
    full_description: '',
    cover_image: '',
    whatsapp: '',
    instagram: '',
    website_url: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const supplierRes = await axios.get(`${API}/suppliers/me`, { withCredentials: true });
      const normalized = normalizeObjectStrings(supplierRes.data);
      setSupplier(normalized);
      setFormData({
        company_name: normalized.company_name || '',
        name: normalized.name || normalized.contact_name || '',
        phone: normalized.phone || '',
        category: normalized.category || '',
        city: normalized.city || '',
        state: normalized.state || '',
        short_description: normalized.short_description || '',
        full_description: normalized.full_description || '',
        cover_image: normalized.cover_image || '',
        whatsapp: normalized.whatsapp || '',
        instagram: normalized.instagram || '',
        website_url: normalized.website_url || '',
      });
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/fornecedor/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/suppliers/logout`, {}, { withCredentials: true });
    } catch (error) {
      // noop
    }
    navigate('/fornecedor/login');
    toast.success('Sessao encerrada.');
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await axios.put(`${API}/suppliers/profile`, formData, { withCredentials: true });
      const normalized = normalizeObjectStrings(response.data);
      setSupplier(normalized);
      setFormData((prev) => ({
        ...prev,
        company_name: normalized.company_name || '',
        name: normalized.name || normalized.contact_name || '',
      }));
      toast.success('Perfil atualizado com sucesso.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil.');
    } finally {
      setSaving(false);
    }
  };

  if (!SUPPLIERS_BETA_ENABLED) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F1EF] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#E8B7B1] border-t-transparent" />
          <p className="text-[#5B4A45]">Carregando area de fornecedores...</p>
        </div>
      </div>
    );
  }

  const approvalStatus = (supplier?.approval_status || 'pending_review').toLowerCase();
  const approvalMeta = APPROVAL_META[approvalStatus] || APPROVAL_META.pending_review;
  const ApprovalIcon = approvalMeta.icon;
  const planMeta = getSupplierPlanMeta(supplier?.plan_type);
  const profileUrl = supplier?.slug ? `${window.location.origin}/fornecedores/${supplier.slug}` : '';
  const publicationChecklist = [
    Boolean(formData.company_name.trim()),
    Boolean(formData.category.trim()),
    Boolean(formData.city.trim()),
    Boolean(formData.state.trim()),
    Boolean(formData.short_description.trim()),
    Boolean((formData.whatsapp || formData.instagram || formData.website_url).trim()),
  ];
  const checklistDone = publicationChecklist.filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#F6F1EF]">
      <header className="border-b border-[#E8B7B1]/35 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <BrandLogo size="sm" />
            </Link>
            <span className="hidden text-sm text-[#5B4A45] sm:inline">| Area de Fornecedores</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#5B4A45] md:inline">{supplier?.public_name || supplier?.company_name}</span>
            <Button variant="outline" onClick={handleLogout} className="rounded-full" data-testid="supplier-logout-btn">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-8">
          <p className="mb-2 text-sm uppercase tracking-[0.3em] text-[#B78D76]">Area de fornecedores</p>
          <h1 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ola, {supplier?.public_name || supplier?.company_name || 'fornecedor'}!
          </h1>
          <p className="mt-2 max-w-3xl text-[#5B4A45]">
            Ajuste os dados do seu perfil, acompanhe a aprovacao e prepare sua divulgacao dentro do diretorio do Virei Noiva.
          </p>
        </div>

        <section className={`mb-8 rounded-[2rem] border p-6 shadow-sm ${approvalMeta.className}`}>
          <div className="flex items-start gap-4">
            <ApprovalIcon className="mt-1 h-6 w-6 flex-shrink-0" />
            <div className="flex-1">
              <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                {approvalMeta.title}
              </h2>
              <p className="mt-2 max-w-3xl text-sm md:text-base">{approvalMeta.description}</p>
              {supplier?.approval_reason ? (
                <p className="mt-3 rounded-xl border border-current/15 bg-white/60 px-4 py-3 text-sm">
                  Observacao da equipe: <strong>{supplier.approval_reason}</strong>
                </p>
              ) : null}
            </div>
          </div>
        </section>

        <section className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
            <p className="text-sm uppercase tracking-[0.2em] text-[#7A5D52]">Plano atual</p>
            <p className={`mt-3 text-3xl font-semibold ${planMeta.accentClass}`}>{planMeta.label}</p>
            <p className="mt-2 text-sm text-[#5B4A45]">Sua visibilidade segue esse plano.</p>
          </div>

          <div className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
            <p className="text-sm uppercase tracking-[0.2em] text-[#7A5D52]">Publicacao</p>
            <p className="mt-3 text-3xl font-semibold text-[#1C1C1C]">{supplier?.is_marketplace_published ? 'Ativa' : 'Desligada'}</p>
            <p className="mt-2 text-sm text-[#5B4A45]">Aprovado + publicacao ativa = aparece no diretorio.</p>
          </div>

          <div className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
            <p className="text-sm uppercase tracking-[0.2em] text-[#7A5D52]">Destaque na home</p>
            <p className="mt-3 text-3xl font-semibold text-[#1C1C1C]">{supplier?.is_homepage_featured ? 'Em destaque' : 'Rotacao normal'}</p>
            <p className="mt-2 text-sm text-[#5B4A45]">Perfis destacados podem ganhar posicoes especiais na homepage.</p>
          </div>

          <div className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
            <p className="text-sm uppercase tracking-[0.2em] text-[#7A5D52]">Checklist do perfil</p>
            <p className="mt-3 text-3xl font-semibold text-[#1C1C1C]">{checklistDone}/6</p>
            <p className="mt-2 text-sm text-[#5B4A45]">Quanto mais completo, mais facil publicar e destacar.</p>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <form onSubmit={handleSave} className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-6 shadow-[0_20px_55px_rgba(96,72,58,0.08)]">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.25em] text-[#B78D76]">Meu perfil</p>
                <h2 className="mt-2 text-2xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dados publicos do fornecedor
                </h2>
              </div>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#E8B7B1] text-white hover:bg-[#DFA8A1]">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Salvando...' : 'Salvar perfil'}
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Empresa ou marca</Label>
                <Input value={formData.company_name} onChange={(event) => setFormData((prev) => ({ ...prev, company_name: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Responsavel</Label>
                <Input value={formData.name} onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Categoria</Label>
                <select value={formData.category} onChange={(event) => setFormData((prev) => ({ ...prev, category: event.target.value }))} className="h-12 w-full rounded-xl border border-[#E8B7B1]/35 bg-[#F9F5F3] px-4 text-[#1C1C1C]">
                  <option value="">Escolha a categoria</option>
                  {SUPPLIER_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Cidade</Label>
                <Input value={formData.city} onChange={(event) => setFormData((prev) => ({ ...prev, city: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Estado</Label>
                <select value={formData.state} onChange={(event) => setFormData((prev) => ({ ...prev, state: event.target.value }))} className="h-12 w-full rounded-xl border border-[#E8B7B1]/35 bg-[#F9F5F3] px-4 text-[#1C1C1C]">
                  <option value="">UF</option>
                  {BRAZIL_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Descricao curta</Label>
              <Textarea value={formData.short_description} onChange={(event) => setFormData((prev) => ({ ...prev, short_description: event.target.value }))} className="min-h-[110px] rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
            </div>

            <div className="mt-4">
              <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Descricao completa</Label>
              <Textarea value={formData.full_description} onChange={(event) => setFormData((prev) => ({ ...prev, full_description: event.target.value }))} className="min-h-[150px] rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(event) => setFormData((prev) => ({ ...prev, whatsapp: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Instagram</Label>
                <Input value={formData.instagram} onChange={(event) => setFormData((prev) => ({ ...prev, instagram: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Site</Label>
                <Input value={formData.website_url} onChange={(event) => setFormData((prev) => ({ ...prev, website_url: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-bold uppercase tracking-wider text-[#1C1C1C]">Imagem de capa (URL)</Label>
                <Input value={formData.cover_image} onChange={(event) => setFormData((prev) => ({ ...prev, cover_image: event.target.value }))} className="h-12 rounded-xl border-[#E8B7B1]/35 bg-[#F9F5F3]" />
              </div>
            </div>
          </form>

          <div className="space-y-6">
            <section className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-6 shadow-[0_20px_55px_rgba(96,72,58,0.08)]">
              <p className="text-sm uppercase tracking-[0.25em] text-[#B78D76]">Visibilidade</p>
              <div className="mt-5 space-y-4 text-sm text-[#5B4A45]">
                <div className="flex items-start gap-3 rounded-[1.25rem] bg-[#F9F5F3] p-4">
                  <Store className="mt-0.5 h-5 w-5 text-[#C8A97E]" />
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">Diretorio publico</p>
                    <p>Apenas fornecedores aprovados e publicados aparecem em `/fornecedores`.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[1.25rem] bg-[#F9F5F3] p-4">
                  <Sparkles className="mt-0.5 h-5 w-5 text-[#C8A97E]" />
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">Destaque na home</p>
                    <p>Perfis com mais potencial comercial podem entrar em blocos especiais na homepage.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[1.25rem] bg-[#F9F5F3] p-4">
                  <Users className="mt-0.5 h-5 w-5 text-[#C8A97E]" />
                  <div>
                    <p className="font-semibold text-[#1C1C1C]">Proximo passo comercial</p>
                    <p>Planos Premium e Premium Plus aumentam prioridade e exposicao dentro da vitrine.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-6 shadow-[0_20px_55px_rgba(96,72,58,0.08)]">
              <p className="text-sm uppercase tracking-[0.25em] text-[#B78D76]">Perfil publico</p>
              <div className="mt-4 space-y-3 text-sm text-[#5B4A45]">
                <p><strong>Slug:</strong> {supplier?.slug || 'sera gerado ao aprovar/publicar'}</p>
                <p><strong>Categoria:</strong> {supplier?.category || 'Nao definida'}</p>
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#C8A97E]" /> {supplier?.city || '-'} {supplier?.state ? `, ${supplier.state}` : ''}</p>
                {profileUrl ? (
                  <a href={profileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm font-medium text-[#B97D5B]">
                    <Eye className="mr-2 h-4 w-4" />
                    Abrir perfil publico
                    <ArrowUpRight className="ml-1.5 h-4 w-4" />
                  </a>
                ) : null}
              </div>
            </section>

            <section className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-6 shadow-[0_20px_55px_rgba(96,72,58,0.08)]">
              <p className="text-sm uppercase tracking-[0.25em] text-[#B78D76]">Planos de visibilidade</p>
              <div className="mt-4 space-y-3">
                {SUPPLIER_PLAN_OPTIONS.map((plan) => {
                  const meta = getSupplierPlanMeta(plan.id);
                  const isCurrent = String(supplier?.plan_type || 'free').toLowerCase() === plan.id;
                  return (
                    <article key={plan.id} className={`rounded-[1.25rem] border p-4 ${meta.cardClass}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">{plan.eyebrow}</p>
                          <p className="mt-1 text-lg font-semibold text-[#1C1C1C]">{plan.name}</p>
                        </div>
                        {isCurrent ? (
                          <span className="rounded-full bg-[#1C1C1C] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                            Plano atual
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-[#4B403D]">{plan.description}</p>
                    </article>
                  );
                })}
              </div>
              <p className="mt-4 text-sm text-[#5B4A45]">
                Upgrades de plano podem ser definidos manualmente pela equipe para testar visibilidade e resposta comercial.
              </p>
            </section>

            {supplier?.partner_coupon_code ? (
              <section className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-6 shadow-[0_20px_55px_rgba(96,72,58,0.08)]">
                <p className="text-sm uppercase tracking-[0.25em] text-[#B78D76]">Cupom do parceiro</p>
                <p className="mt-3 text-2xl font-semibold text-[#1C1C1C]">{supplier.partner_coupon_code}</p>
                <p className="mt-2 text-sm text-[#5B4A45]">
                  Esse cupom continua ativo para iniciativas de parceria enquanto a vitrine de fornecedores evolui.
                </p>
              </section>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}
